import React from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, ShoppingBag } from "lucide-react";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { buildWhatsappLink, track } from "../lib/analytics";
import { inr } from "../lib/api";

export default function ProductCard({ product, index = 0, listName = "shop" }) {
    const { has, toggle } = useWishlist();
    const { add } = useCart();
    const { settings } = useSettings();
    const liked = has(product.id);
    const oos = product.inStock === false || product.stockStatus === "out_of_stock";

    const img = product.images?.[0];
    const img2 = product.images?.[1] || img;

    const waLink = buildWhatsappLink({
        number: settings?.whatsappNumber || "919999999999",
        template: product.whatsappMessageTemplate || settings?.defaultWhatsappTemplate,
        product,
    });

    const onWhatsapp = (e) => {
        e.preventDefault();
        e.stopPropagation();
        track("whatsapp_click", {
            item_id: product.id, item_name: product.name, price: product.price,
            item_category: product.category, list_name: listName,
        });
        window.open(waLink, "_blank", "noopener");
    };

    const onSelect = () => {
        track("select_item", {
            item_id: product.id, item_name: product.name, price: product.price,
            item_category: product.category, list_name: listName, index,
        });
    };

    return (
        <div className="group" data-testid={`product-card-${product.slug}`}>
            <Link to={`/product/${product.slug}`} onClick={onSelect} className="block">
                <div className="relative overflow-hidden rounded-2xl bg-ivory-100 aspect-[4/5]">
                    <img
                        src={img}
                        alt={product.name}
                        className="absolute inset-0 w-full h-full object-cover editorial-img"
                        loading="lazy"
                    />
                    <img
                        src={img2}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                        loading="lazy"
                    />
                    <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(product); }}
                        className={`absolute top-4 right-4 h-9 w-9 rounded-full flex items-center justify-center bg-white/90 backdrop-blur ${liked ? "text-blush-500" : "text-ink-700"} hover:text-blush-500 transition-colors shadow-sm`}
                        aria-label="Wishlist"
                        data-testid={`wishlist-toggle-${product.slug}`}
                    >
                        <Heart size={16} strokeWidth={1.5} fill={liked ? "currentColor" : "none"} />
                    </button>

                    {product.compareAtPrice && product.compareAtPrice > product.price && (
                        <span className="absolute top-4 left-4 bg-blush-300 text-ink-900 text-[10px] uppercase tracking-[0.22em] px-3 py-1 rounded-full">
                            Sale
                        </span>
                    )}
                </div>

                <div className="pt-4 px-1">
                    <h3 className="font-serif text-lg text-ink-900 leading-snug">{product.name}</h3>
                    {product.subtitle && <p className="text-xs text-ink-500 mt-0.5">{product.subtitle}</p>}
                    <div className="flex items-baseline gap-2 mt-2">
                        <span className="text-sage-700 font-medium">{inr(product.price)}</span>
                        {product.compareAtPrice && product.compareAtPrice > product.price && (
                            <span className="text-xs text-ink-500 line-through">{inr(product.compareAtPrice)}</span>
                        )}
                    </div>
                    {product.sizes?.length > 0 && (
                        <p className="text-[11px] text-ink-500 mt-1.5 tracking-wider uppercase">
                            {product.sizes.slice(0, 4).join(" · ")}
                        </p>
                    )}
                </div>
            </Link>

            <div className="mt-3 flex gap-2">
                <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); add(product, { size: product.sizes?.[0] }); }}
                    disabled={oos}
                    className={`flex-1 text-center rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors inline-flex items-center justify-center gap-1.5 ${oos ? "bg-ivory-100 text-ink-500 cursor-not-allowed" : "bg-sage-400 hover:bg-sage-500 text-white"}`}
                    data-testid={`add-to-cart-${product.slug}`}
                >
                    <ShoppingBag size={12} />{oos ? "Sold Out" : "Add to Bag"}
                </button>
                <button
                    onClick={onWhatsapp}
                    className="h-9 w-9 rounded-full border border-sage-300/40 hover:bg-sage-50 text-sage-700 flex items-center justify-center transition-colors"
                    aria-label="Ask on WhatsApp"
                    data-testid={`whatsapp-card-${product.slug}`}
                >
                    <MessageCircle size={15} strokeWidth={1.5} />
                </button>
            </div>
        </div>
    );
}
